import React, { useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";

// Inline SVG icons
const EnvelopeIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...props}>
    <path strokeWidth={2} d="M4 6h16v12H4z" />
    <path strokeWidth={2} d="M22 6l-10 7L2 6" />
  </svg>
);
const LockClosedIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...props}>
    <rect x={5} y={10} width={14} height={10} rx={2} strokeWidth={2} />
    <path d="M7 10V8a5 5 0 0110 0v2" strokeWidth={2} />
  </svg>
);
const EyeIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...props}>
    <path strokeWidth={2} d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
    <circle cx={12} cy={12} r={3} strokeWidth={2} />
  </svg>
);
const EyeSlashIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...props}>
    <path
      d="M17.94 17.94A10.94 10.94 0 0112 19c-7 0-11-7-11-7a20.46 20.46 0 014.22-4.92"
      strokeWidth={2}
    />
    <path d="M1 1l22 22" strokeWidth={2} />
    <circle cx={12} cy={12} r={3} strokeWidth={2} />
  </svg>
);

export default function Auth() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shake, setShake] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const { signIn } = useAuth();

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      setIsSubmitting(true);
      await signIn(email, password);
    } catch (err: any) {
      setError(err.message || "Erro de autenticação");
      setShake(true);
      setTimeout(() => setShake(false), 600);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex items-center justify-center w-full min-h-screen p-4 login-bg">
      <div
        ref={cardRef}
        className={`relative z-10 w-full max-w-md shadow-2xl rounded-2xl overflow-hidden animate-fade-in ${
          shake ? "animate-shake" : ""
        }`}
        style={{ minHeight: 520 }}
      >
        <div
          className="w-full flex items-center justify-center p-8 card-surface"
          style={{ minHeight: 550 }}
        >
          <form onSubmit={onSubmit} className="w-full max-w-md">
            <h1 className="text-3xl font-bold text-center text-gray-800 mb-2">
              Feed Control
            </h1>
            <p className="text-center text-gray-600 mb-8">
              Sign in to continue
            </p>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <div className="relative">
                <EnvelopeIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  className="w-full pl-10 pr-4 py-3 bg-white border border-[#E6EEF8] rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-400/50 transition-all shadow-sm"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              {error && error.toLowerCase().includes("email") && (
                <p className="mt-1 text-xs text-red-500">{error}</p>
              )}
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <LockClosedIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showPassword ? "text" : "password"}
                  className="w-full pl-10 pr-10 py-3 bg-white border border-[#E6EEF8] rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-400/50 transition-all shadow-sm"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                  tabIndex={-1}
                  onClick={() => setShowPassword((v) => !v)}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="w-5 h-5" />
                  ) : (
                    <EyeIcon className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {error && !error.toLowerCase().includes("email") && (
              <div className="mt-4 bg-red-50 text-red-700 p-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full mt-6 bg-[#0186FF] hover:bg-blue-700 text-white font-semibold rounded-lg py-3 px-4 transition-all duration-200 shadow focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8z"
                    ></path>
                  </svg>
                  Signing in...
                </span>
              ) : (
                "Sign in"
              )}
            </button>
          </form>
        </div>
      </div>
      <style>{`
        .animate-fade-in { animation: fadeIn 0.8s cubic-bezier(.4,0,.2,1) both; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(40px); } to { opacity: 1; transform: none; } }
        .animate-shake { animation: shake 0.6s cubic-bezier(.36,.07,.19,.97) both; }
        @keyframes shake {
          10%, 90% { transform: translateX(-2px); }
          20%, 80% { transform: translateX(4px); }
          30%, 50%, 70% { transform: translateX(-8px); }
          40%, 60% { transform: translateX(8px); }
        }
        .login-bg {
          background:
            radial-gradient(900px 600px at 20% 15%, rgba(255,255,255,0.7), rgba(255,255,255,0) 40%),
            linear-gradient(135deg, #F3F8FF 0%, #DFEEFF 100%);
        }
        .card-surface {
          background: linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(236,244,255,0.98) 100%);
          border: 1px solid rgba(230, 238, 248, 0.9);
          box-shadow: 0 20px 60px rgba(16, 24, 40, 0.12);
          border-radius: 1rem;
        }
      `}</style>
    </div>
  );
}
